import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Grid,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import StorefrontIcon from '@mui/icons-material/Storefront';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

import { fetchTrendArticle, fetchTrendRecipeByArticle, generateTrendRecipeForArticle, updateTrendArticle } from '../service';
import type { TrendQualityScope } from '../service';
import type { TrendArticle, TrendRecipe } from '../types';
import { extractTrendUiErrorMessage } from '../errorUtils';
import { filterMeaningfulTrendStrings, getMeaningfulTrendText } from '../contentQuality';
import {
  DISPLAY_FONT,
  HEADING_SX,
  SECTION_HEADING_SX,
  TREND_COLORS,
  getArticleImage,
  getDesignSubCategoryLabel,
  getFallbackArticleImage,
} from '../trendTheme';

type TrendArticleDetailDialogProps = {
  articleId: string | null;
  open: boolean;
  onClose: () => void;
  editable?: boolean;
  qualityScope?: TrendQualityScope;
  onUpdated?: (article: TrendArticle) => void;
};

type TrendArticleReviewDraft = {
  title: string;
  articleIntro: string;
  articleBody: string;
  whyItMatters: string;
  whatItMeansForUs: string;
  dvhAngle: string;
  foodserviceAngle: string;
  productConnections: string;
  recipeConnections: string;
  recommendedActions: string;
  editorComment: string;
};

function joinMultilineDraft(values: string[] | undefined): string {
  return Array.isArray(values) ? values.filter(Boolean).join('\n') : '';
}

function splitMultilineDraft(value: string): string[] {
  return String(value || '')
    .split('\n')
    .map((entry) => entry.trim())
    .filter(Boolean);
}

function buildReviewDraft(article: TrendArticle | null): TrendArticleReviewDraft {
  return {
    title: String(article?.title || ''),
    articleIntro: String(article?.articleSections?.articleIntro || article?.excerpt || ''),
    articleBody: String(article?.articleSections?.articleBody || article?.body || ''),
    whyItMatters: String(article?.fontanaAnalysis?.whyItMatters || article?.whyItMatters || ''),
    whatItMeansForUs: String(article?.fontanaAnalysis?.whatItMeansForUs || article?.whatItMeansForUs || ''),
    dvhAngle: String(article?.fontanaAnalysis?.dvhAngle || article?.dvhAngle || ''),
    foodserviceAngle: String(article?.fontanaAnalysis?.foodserviceAngle || article?.foodserviceAngle || ''),
    productConnections: joinMultilineDraft(article?.fontanaAnalysis?.productConnections || article?.productConnections),
    recipeConnections: joinMultilineDraft(article?.fontanaAnalysis?.recipeConnections || article?.recipeConnections),
    recommendedActions: joinMultilineDraft(article?.fontanaAnalysis?.recommendedActions || article?.recommendedActions),
    editorComment: String(article?.editorComment || ''),
  };
}

function getArticleReviewReasons(article: TrendArticle | null): string[] {
  if (!article) return [];
  return Array.from(new Set([
    ...(article.publicationQuality?.reasons || []),
    ...(article.activationQuality?.reasons || []),
  ]));
}

function getArticleQualityLabel(status: TrendArticle['qualityStatus']): string {
  if (status === 'requires_review') return 'KRÄVER GRANSKNING';
  if (status === 'blocked') return 'BLOCKERAD';
  return 'PUBLICERBAR';
}

function getArticleQualityChipSx(status: TrendArticle['qualityStatus']) {
  if (status === 'requires_review') {
    return { bgcolor: '#FFF4D6', color: '#8A5D00', fontWeight: 700 };
  }
  if (status === 'blocked') {
    return { bgcolor: '#FDE8E8', color: '#C53030', fontWeight: 700 };
  }
  return { bgcolor: TREND_COLORS.oliveLight, color: TREND_COLORS.olive, fontWeight: 700 };
}

function getSourceContentStatusLabel(status: NonNullable<TrendArticle['sourceItems']>[number]['contentStatus']): string {
  if (status === 'full_article_text') return 'Full artikeltext';
  if (status === 'teaser_snippet') return 'Teaser/snippet';
  if (status === 'boilerplate_dominated') return 'Boilerplate';
  if (status === 'requires_review') return 'Kräver granskning';
  return 'Okänd underlagstyp';
}

function getSourceReviewStatusLabel(status: NonNullable<TrendArticle['sourceItems']>[number]['reviewStatus']): string {
  if (status === 'required') return 'Granskning krävs';
  if (status === 'completed') return 'Granskning klar';
  return 'Ingen granskning krävs';
}

function getSourceExtractionMethodLabel(
  method: NonNullable<NonNullable<TrendArticle['sourceItems']>[number]['extractionMetadata']>['method'],
): string {
  if (method === 'feed_readability') return 'Extraktion: feed + readability';
  if (method === 'feed_html_strip') return 'Extraktion: feed + HTML-strip';
  if (method === 'http_readability') return 'Extraktion: HTTP + readability';
  if (method === 'http_html_strip') return 'Extraktion: HTTP + HTML-strip';
  if (method === 'headless_readability') return 'Extraktion: headless + readability';
  if (method === 'headless_html_strip') return 'Extraktion: headless + HTML-strip';
  if (method === 'raw_text_only') return 'Extraktion: råtext';
  return 'Extraktion: okänd';
}

function formatTrendDisplayDate(value?: string | null): string {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleDateString('sv-SE', { day: 'numeric', month: 'long', year: 'numeric' });
}

function getArticleEvidenceStatusLabel(status?: TrendArticle['evidenceStatus']): string {
  if (status === 'actionable_opportunity') return 'Aktiverbar möjlighet';
  if (status === 'validated_trend') return 'Validerad trend';
  return 'Svagt trendtecken';
}

function getArticleProvenanceLabel(provenance?: TrendArticle['provenance']): string {
  if (provenance === 'feed+discovery') return 'Feed + discovery';
  if (provenance === 'discovery') return 'Discovery';
  return 'Feed';
}

function getArticleEvidenceImages(article: TrendArticle | null): string[] {
  if (!article) return [];
  const urls = [article.heroImage, ...article.galleryImages]
    .map((entry) => String(entry || '').trim())
    .filter((entry) => /^https?:\/\//i.test(entry));
  return Array.from(new Set(urls));
}

export default function TrendArticleDetailDialog({
  articleId,
  open,
  onClose,
  editable = false,
  qualityScope = 'publishable',
  onUpdated,
}: TrendArticleDetailDialogProps) {
  const [article, setArticle] = useState<TrendArticle | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [heroImageDraft, setHeroImageDraft] = useState('');
  const [saveState, setSaveState] = useState<{ loading: boolean; error: string | null; success: string | null }>({
    loading: false,
    error: null,
    success: null,
  });
  const [recipe, setRecipe] = useState<TrendRecipe | null>(null);
  const [recipeGenerating, setRecipeGenerating] = useState(false);
  const [reviewDraft, setReviewDraft] = useState<TrendArticleReviewDraft>(buildReviewDraft(null));

  useEffect(() => {
    if (!open || !articleId) {
      setArticle(null);
      setError(null);
      setHeroImageDraft('');
      setRecipe(null);
      setRecipeGenerating(false);
      setSaveState({ loading: false, error: null, success: null });
      return;
    }

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const next = await fetchTrendArticle(articleId, qualityScope);
        if (!cancelled) {
          setArticle(next);
          setHeroImageDraft(next.heroImage || '');
          setReviewDraft(buildReviewDraft(next));
        }
        if (next.hasRecipe || next.trendRecipeId) {
          const existingRecipe = await fetchTrendRecipeByArticle(articleId);
          if (!cancelled) setRecipe(existingRecipe);
        }
      } catch (err: any) {
        if (!cancelled) {
          setError(extractTrendUiErrorMessage(err, 'Kunde inte ladda trendartikeln.'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => { cancelled = true; };
  }, [articleId, open, qualityScope]);

  const imageUrl = article ? getArticleImage(article.heroImage, article.category) : '';
  const fallbackImageUrl = getFallbackArticleImage(article?.category);
  const articleCategory = String(article?.category || '').trim().toLowerCase();
  const isFoodArticle = articleCategory === 'food' || articleCategory === 'consumer';
  const primarySourceReference = article?.sourceReferences?.[0] || null;
  const primarySourceItem = article?.sourceItems?.[0] || null;
  const articleIntro = article?.articleSections?.articleIntro || article?.excerpt || '';
  const articleBody = article?.articleSections?.articleBody || article?.body || '';
  const rawBodySections = article?.articleSections?.bodySections ?? [];
  const bodySections = rawBodySections
    .filter((section) => section.heading || section.body);
  const keyPoints = article?.articleSections?.keyPoints || [];
  const sourceFacts = article?.articleSections?.sourceFacts || [];
  const sourceWarnings = article?.articleSections?.sourceWarnings || [];
  const visualInsights = article?.articleSections?.visualInsights || [];
  const fontanaWhyItMatters = getMeaningfulTrendText(
    article?.fontanaAnalysis?.whyItMatters || article?.whyItMatters || '',
    { minLength: 70 },
  );
  const fontanaMeaning = getMeaningfulTrendText(
    article?.fontanaAnalysis?.whatItMeansForUs || article?.whatItMeansForUs || '',
    { minLength: 80 },
  );
  const fontanaDvh = getMeaningfulTrendText(
    article?.fontanaAnalysis?.dvhAngle || article?.dvhAngle || '',
    { minLength: 60 },
  );
  const fontanaFoodservice = getMeaningfulTrendText(
    article?.fontanaAnalysis?.foodserviceAngle || article?.foodserviceAngle || '',
    { minLength: 60 },
  );
  const productConnections = filterMeaningfulTrendStrings(
    article?.fontanaAnalysis?.productConnections?.length
    ? article.fontanaAnalysis.productConnections
    : (article?.productConnections || []),
    'product',
  );
  const recipeConnections = filterMeaningfulTrendStrings(
    article?.fontanaAnalysis?.recipeConnections?.length
    ? article.fontanaAnalysis.recipeConnections
    : (article?.recipeConnections || []),
    'recipe',
  );
  const recommendedActions = filterMeaningfulTrendStrings(
    article?.fontanaAnalysis?.recommendedActions?.length
    ? article.fontanaAnalysis.recommendedActions
    : (article?.recommendedActions || []),
    'action',
  );
  const fontanaCommercialHypothesis = getMeaningfulTrendText(article?.fontanaAnalysis?.commercialHypothesis || '', { minLength: 30 });
  const fontanaFirstTest = getMeaningfulTrendText(article?.fontanaAnalysis?.firstTest || '', { minLength: 20 });
  const fontanaDepartmentOwner = String(article?.fontanaAnalysis?.departmentOwner || '').trim();
  const fontanaTimeHorizonRaw = String(article?.fontanaAnalysis?.timeHorizon || '').trim().toLowerCase();
  const fontanaTimeHorizon = fontanaTimeHorizonRaw === 'now'
    ? 'Nu'
    : fontanaTimeHorizonRaw === 'next'
      ? 'Nästa'
      : fontanaTimeHorizonRaw === 'later'
        ? 'Senare'
        : '';
  const fontanaEvidenceCaveat = getMeaningfulTrendText(article?.fontanaAnalysis?.evidenceCaveat || '', { minLength: 20 });
  const hasActivationDetails = Boolean(
    fontanaCommercialHypothesis || fontanaFirstTest || fontanaDepartmentOwner || fontanaTimeHorizon || fontanaEvidenceCaveat,
  );
  const designObservedVisualCue = String(article?.designAnalysis?.observedVisualCue || '').trim();
  const designShelfImpact = String(article?.designAnalysis?.shelfImpact || '').trim();
  const designPackagingImplication = String(article?.designAnalysis?.packagingImplication || '').trim();
  const designBrandRisk = String(article?.designAnalysis?.brandRisk || '').trim();
  const designExampleToWatch = String(article?.designAnalysis?.exampleToWatch || '').trim();
  const hasDesignAnalysis = Boolean(
    designObservedVisualCue || designShelfImpact || designPackagingImplication || designBrandRisk || designExampleToWatch,
  );
  const hasFontanaSection = Boolean(fontanaMeaning || fontanaWhyItMatters);
  const hasChannelAngles = Boolean(fontanaDvh || fontanaFoodservice);
  const hasNextSteps = recommendedActions.length > 0;
  const evidenceImages = getArticleEvidenceImages(article);
  const originalHeadline = primarySourceItem?.extractionMetadata?.title
    || primarySourceReference?.title
    || primarySourceItem?.title
    || '';
  const primarySourceName = primarySourceItem?.extractionMetadata?.siteName
    || primarySourceReference?.sourceName
    || '';
  const primarySourceDate = formatTrendDisplayDate(primarySourceReference?.publishedAt || article?.publishedAt || null);
  const primarySourceByline = primarySourceItem?.extractionMetadata?.byline || '';
  const primarySourceExcerpt = primarySourceItem?.extractionMetadata?.excerpt || '';
  const evidenceMetaLine = [primarySourceName, primarySourceDate, primarySourceByline].filter(Boolean).join(' · ');
  const articleReviewReasons = getArticleReviewReasons(article);

  const updateReviewDraft = (field: keyof TrendArticleReviewDraft, value: string) => {
    setReviewDraft((prev) => ({ ...prev, [field]: value }));
    setSaveState((prev) => ({ ...prev, error: null, success: null }));
  };

  const handleSaveHeroImage = async (nextHeroImage: string) => {
    if (!article) return;

    setSaveState({ loading: true, error: null, success: null });
    try {
      await updateTrendArticle(article._id, { heroImage: nextHeroImage.trim() });
      const refreshed = await fetchTrendArticle(article._id, qualityScope);
      setArticle(refreshed);
      setHeroImageDraft(refreshed.heroImage || '');
      setReviewDraft(buildReviewDraft(refreshed));
      setSaveState({ loading: false, error: null, success: 'Bild uppdaterad.' });
      onUpdated?.(refreshed);
    } catch (err: any) {
      setSaveState({
        loading: false,
        error: extractTrendUiErrorMessage(err, 'Kunde inte uppdatera bilden.'),
        success: null,
      });
    }
  };

  const handleSaveReview = async () => {
    if (!article) return;

    const productConnections = splitMultilineDraft(reviewDraft.productConnections);
    const recipeConnections = splitMultilineDraft(reviewDraft.recipeConnections);
    const recommendedActionsDraft = splitMultilineDraft(reviewDraft.recommendedActions);

    setSaveState({ loading: true, error: null, success: null });
    try {
      await updateTrendArticle(article._id, {
        title: reviewDraft.title,
        excerpt: reviewDraft.articleIntro,
        body: reviewDraft.articleBody,
        articleSections: {
          ...article.articleSections,
          articleIntro: reviewDraft.articleIntro,
          articleBody: reviewDraft.articleBody,
        },
        fontanaAnalysis: {
          ...article.fontanaAnalysis,
          whyItMatters: reviewDraft.whyItMatters,
          whatItMeansForUs: reviewDraft.whatItMeansForUs,
          dvhAngle: reviewDraft.dvhAngle,
          foodserviceAngle: reviewDraft.foodserviceAngle,
          productConnections,
          recipeConnections,
          recommendedActions: recommendedActionsDraft,
        },
        whyItMatters: reviewDraft.whyItMatters,
        whatItMeansForUs: reviewDraft.whatItMeansForUs,
        dvhAngle: reviewDraft.dvhAngle,
        foodserviceAngle: reviewDraft.foodserviceAngle,
        productConnections,
        recipeConnections,
        recommendedActions: recommendedActionsDraft,
        editorComment: reviewDraft.editorComment,
      });
      const refreshed = await fetchTrendArticle(article._id, 'all');
      setArticle(refreshed);
      setHeroImageDraft(refreshed.heroImage || '');
      setReviewDraft(buildReviewDraft(refreshed));
      setSaveState({
        loading: false,
        error: null,
        success: refreshed.qualityStatus === 'publishable'
          ? 'Artikeln är nu publicerbar på översiktssidan.'
          : 'Artikeln sparades men kräver fortfarande granskning.',
      });
      onUpdated?.(refreshed);
    } catch (err: any) {
      setSaveState({
        loading: false,
        error: extractTrendUiErrorMessage(err, 'Kunde inte spara granskningsändringarna.'),
        success: null,
      });
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 3,
          bgcolor: TREND_COLORS.pageBg,
          maxHeight: '92vh',
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
            <CircularProgress sx={{ color: TREND_COLORS.navy }} />
          </Box>
        )}
        {error && <Alert severity="error" sx={{ m: 3 }}>{error}</Alert>}

        {!loading && !error && article && (
          <Stack>
            {/* Hero image */}
            <Box sx={{ position: 'relative', pt: '45%', overflow: 'hidden' }}>
              <Box
                component="img"
                src={imageUrl}
                alt=""
                onError={(event: React.SyntheticEvent<HTMLImageElement>) => {
                  if (event.currentTarget.src !== fallbackImageUrl) {
                    event.currentTarget.src = fallbackImageUrl;
                  }
                }}
                sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  background: 'linear-gradient(to top, rgba(7,47,83,0.85) 0%, transparent 60%)',
                }}
              />
              <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, p: { xs: 3, md: 4 } }}>
                <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
                  <Chip
                    label={(article.category || 'TREND').toUpperCase()}
                    size="small"
                    sx={{ bgcolor: TREND_COLORS.gold, color: TREND_COLORS.navy, fontWeight: 700, fontSize: '0.6rem', letterSpacing: '0.1em', borderRadius: 1 }}
                  />
                  <Chip
                    label={article.directActivation ? 'AKTIVERBAR TREND' : 'OMVÄRLDSTREND'}
                    size="small"
                    sx={{
                      bgcolor: article.directActivation ? TREND_COLORS.olive : 'rgba(255,255,255,0.2)',
                      color: '#fff',
                      fontWeight: 700,
                      fontSize: '0.6rem',
                      letterSpacing: '0.1em',
                      borderRadius: 1,
                    }}
                  />
                  <Chip
                    label={getArticleQualityLabel(article.qualityStatus)}
                    size="small"
                    sx={{
                      fontSize: '0.6rem',
                      letterSpacing: '0.1em',
                      borderRadius: 1,
                      ...getArticleQualityChipSx(article.qualityStatus),
                    }}
                  />
                </Stack>
                <Typography
                  variant="h4"
                  sx={{
                    fontFamily: DISPLAY_FONT,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    fontWeight: 700,
                    color: '#fff',
                    lineHeight: 1.15,
                    fontSize: { xs: '1.4rem', md: '1.8rem' },
                  }}
                >
                  {article.title}
                </Typography>
              </Box>
            </Box>

            {/* Content */}
            <Stack spacing={0} sx={{ px: { xs: 3, md: 4 }, py: 3 }}>
              {article.qualityStatus !== 'publishable' && (
                <Alert severity="warning" sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>
                    {article.qualityStatus === 'requires_review'
                      ? 'Kräver redaktionell granskning'
                      : 'Blockerad från publika ytor'}
                  </Typography>
                  <Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 2.5 }}>
                    {articleReviewReasons.map((reason) => (
                      <Typography component="li" key={reason} variant="body2">
                        {reason}
                      </Typography>
                    ))}
                  </Stack>
                </Alert>
              )}

              <Stack spacing={4}>
                {editable && (
                  <Box sx={{ maxWidth: 900 }}>
                    <Typography sx={{ ...SECTION_HEADING_SX, mb: 2 }}>REVIEW OCH REDIGERING</Typography>
                    <Typography variant="body2" sx={{ color: TREND_COLORS.textSecondary, mb: 2 }}>
                      Justera de fält som blockerar artikeln och spara för att ompröva om den kan bli publicerbar på översikten.
                    </Typography>
                    <Stack spacing={2}>
                      <TextField
                        fullWidth
                        label="Rubrik"
                        value={reviewDraft.title}
                        onChange={(event) => updateReviewDraft('title', event.target.value)}
                      />
                      <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        label="Ingress"
                        value={reviewDraft.articleIntro}
                        onChange={(event) => updateReviewDraft('articleIntro', event.target.value)}
                      />
                      <TextField
                        fullWidth
                        multiline
                        minRows={8}
                        label="Själva artikeln"
                        value={reviewDraft.articleBody}
                        onChange={(event) => updateReviewDraft('articleBody', event.target.value)}
                        helperText="Underrubriker (`bodySections`) redigeras inte manuellt här i V1, men artikeltexten och publicerbarheten kan omprövas."
                      />
                      <Grid container spacing={2}>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <TextField
                            fullWidth
                            multiline
                            minRows={4}
                            label="Varför det här är viktigt"
                            value={reviewDraft.whyItMatters}
                            onChange={(event) => updateReviewDraft('whyItMatters', event.target.value)}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <TextField
                            fullWidth
                            multiline
                            minRows={4}
                            label="Vad betyder det för Fontana"
                            value={reviewDraft.whatItMeansForUs}
                            onChange={(event) => updateReviewDraft('whatItMeansForUs', event.target.value)}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <TextField
                            fullWidth
                            multiline
                            minRows={4}
                            label="DVH-vinkel"
                            value={reviewDraft.dvhAngle}
                            onChange={(event) => updateReviewDraft('dvhAngle', event.target.value)}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <TextField
                            fullWidth
                            multiline
                            minRows={4}
                            label="Foodservice-vinkel"
                            value={reviewDraft.foodserviceAngle}
                            onChange={(event) => updateReviewDraft('foodserviceAngle', event.target.value)}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                          <TextField
                            fullWidth
                            multiline
                            minRows={4}
                            label="Produktspår"
                            value={reviewDraft.productConnections}
                            onChange={(event) => updateReviewDraft('productConnections', event.target.value)}
                            helperText="En rad per punkt"
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                          <TextField
                            fullWidth
                            multiline
                            minRows={4}
                            label="Receptspår"
                            value={reviewDraft.recipeConnections}
                            onChange={(event) => updateReviewDraft('recipeConnections', event.target.value)}
                            helperText="En rad per punkt"
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                          <TextField
                            fullWidth
                            multiline
                            minRows={4}
                            label="Nästa steg"
                            value={reviewDraft.recommendedActions}
                            onChange={(event) => updateReviewDraft('recommendedActions', event.target.value)}
                            helperText="En rad per punkt"
                          />
                        </Grid>
                      </Grid>
                      <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        label="Redaktionell kommentar"
                        value={reviewDraft.editorComment}
                        onChange={(event) => updateReviewDraft('editorComment', event.target.value)}
                      />
                    </Stack>
                  </Box>
                )}

                {articleIntro && (
                  <Box sx={{ maxWidth: 780 }}>
                    <Typography sx={{ ...SECTION_HEADING_SX, mb: 2 }}>DETTA HANDLAR ARTIKELN OM</Typography>
                    <Typography variant="body1" sx={{ color: TREND_COLORS.textPrimary, lineHeight: 1.85 }}>
                      {articleIntro}
                    </Typography>
                  </Box>
                )}

                <Box sx={{ maxWidth: 780 }}>
                  <Typography sx={{ ...SECTION_HEADING_SX, mb: 2 }}>SJÄLVA ARTIKELN</Typography>
                  {bodySections.length > 0 ? (
                    <Stack spacing={3}>
                      {bodySections.map((section, index) => (
                        <Box key={`${section.heading}-${index}`}>
                          {section.heading && (
                            <Typography sx={{ ...HEADING_SX, fontSize: '0.78rem', color: TREND_COLORS.navy, mb: 1 }}>
                              {section.heading}
                            </Typography>
                          )}
                          <Typography
                            variant="body1"
                            sx={{ whiteSpace: 'pre-line', lineHeight: 1.9, color: TREND_COLORS.textPrimary }}
                          >
                            {section.body}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  ) : (
                    <Typography
                      variant="body1"
                      sx={{ whiteSpace: 'pre-line', lineHeight: 1.9, color: TREND_COLORS.textPrimary }}
                    >
                      {articleBody || articleIntro || 'Ingen fördjupning tillgänglig än.'}
                    </Typography>
                  )}
                </Box>

                {keyPoints.length > 0 && (
                  <Box>
                    <Typography sx={{ ...SECTION_HEADING_SX, mb: 2 }}>VIKTIGASTE PUNKTERNA</Typography>
                    <Grid container spacing={2}>
                      {keyPoints.map((entry) => (
                        <Grid key={entry} size={{ xs: 12, md: 6 }}>
                          <Box
                            sx={{
                              p: 2,
                              borderRadius: 2,
                              bgcolor: TREND_COLORS.cardBg,
                              border: `1px solid ${TREND_COLORS.divider}`,
                              height: '100%',
                            }}
                          >
                            <Typography variant="body2" sx={{ color: TREND_COLORS.textSecondary, lineHeight: 1.7 }}>
                              {entry}
                            </Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )}

                {(visualInsights.length > 0 || evidenceImages.length > 0) && (
                  <Box>
                    <Typography sx={{ ...SECTION_HEADING_SX, mb: 2 }}>VISUELLA INSIKTER OCH BILDER</Typography>
                    <Grid container spacing={3}>
                      {visualInsights.length > 0 && (
                        <Grid size={{ xs: 12, md: evidenceImages.length > 0 ? 5 : 12 }}>
                          <Box
                            sx={{
                              p: 2.5,
                              borderRadius: 2,
                              bgcolor: TREND_COLORS.cardBg,
                              border: `1px solid ${TREND_COLORS.divider}`,
                              height: '100%',
                            }}
                          >
                            <Typography variant="caption" sx={{ ...HEADING_SX, color: TREND_COLORS.textMuted, display: 'block', mb: 1.5 }}>
                              Visuella datapunkter
                            </Typography>
                            <Stack component="ul" spacing={1.25} sx={{ m: 0, pl: 2 }}>
                              {visualInsights.map((entry) => (
                                <Typography component="li" key={entry} variant="body2" sx={{ color: TREND_COLORS.textSecondary, lineHeight: 1.7 }}>
                                  {entry}
                                </Typography>
                              ))}
                            </Stack>
                          </Box>
                        </Grid>
                      )}
                      {evidenceImages.length > 0 && (
                        <Grid size={{ xs: 12, md: visualInsights.length > 0 ? 7 : 12 }}>
                          <Stack direction="row" spacing={1.5} sx={{ overflowX: 'auto', pb: 1 }}>
                            {evidenceImages.map((imgUrl, index) => (
                              <Box
                                key={imgUrl}
                                sx={{
                                  width: { xs: 220, md: 260 },
                                  flexShrink: 0,
                                  borderRadius: 2,
                                  overflow: 'hidden',
                                  bgcolor: TREND_COLORS.cardBg,
                                  border: `1px solid ${TREND_COLORS.divider}`,
                                }}
                              >
                                <Box
                                  component="img"
                                  src={imgUrl}
                                  alt=""
                                  onError={(event: React.SyntheticEvent<HTMLImageElement>) => {
                                    if (event.currentTarget.parentElement) {
                                      event.currentTarget.parentElement.style.display = 'none';
                                    }
                                  }}
                                  sx={{
                                    width: '100%',
                                    height: 170,
                                    objectFit: 'cover',
                                    display: 'block',
                                  }}
                                />
                                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ p: 1.25 }}>
                                  <Chip
                                    size="small"
                                    label={index === 0 ? 'Huvudbild' : `Artikelbild ${index + 1}`}
                                    sx={{ bgcolor: TREND_COLORS.softNavy, color: TREND_COLORS.navy, fontWeight: 600, fontSize: '0.65rem' }}
                                  />
                                </Stack>
                              </Box>
                            ))}
                          </Stack>
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                )}

                {hasFontanaSection && (
                  <Box
                    sx={{
                      bgcolor: TREND_COLORS.softNavy,
                      borderRadius: 2.5,
                      p: 3,
                    }}
                  >
                    <Typography sx={{ ...SECTION_HEADING_SX, borderBottomColor: TREND_COLORS.navy, mb: 1.5 }}>
                      VAD BETYDER DETTA FÖR FONTANA?
                    </Typography>
                    <Typography variant="body1" sx={{ color: TREND_COLORS.navy, lineHeight: 1.85 }}>
                      {fontanaMeaning || fontanaWhyItMatters}
                    </Typography>
                    {fontanaWhyItMatters && fontanaWhyItMatters !== fontanaMeaning && (
                      <Box
                        sx={{
                          mt: 2,
                          p: 2,
                          borderRadius: 2,
                          bgcolor: 'rgba(255,255,255,0.72)',
                        }}
                      >
                        <Typography variant="caption" sx={{ ...HEADING_SX, color: TREND_COLORS.textMuted, display: 'block', mb: 0.75 }}>
                          Varför det spelar roll
                        </Typography>
                        <Typography variant="body2" sx={{ color: TREND_COLORS.navy, lineHeight: 1.75 }}>
                          {fontanaWhyItMatters}
                        </Typography>
                      </Box>
                    )}
                    {isFoodArticle && article.fontanaPriorityIngredients.length > 0 && (
                      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
                        {article.fontanaPriorityIngredients.map((ingredient) => (
                          <Chip
                            key={ingredient}
                            label={ingredient}
                            size="small"
                            sx={{ bgcolor: TREND_COLORS.navyLight, color: '#fff', fontSize: '0.65rem', fontWeight: 600 }}
                          />
                        ))}
                      </Stack>
                    )}
                  </Box>
                )}

                {hasChannelAngles && (
                  <Grid container spacing={3}>
                    {fontanaDvh && (
                      <Grid size={{ xs: 12, md: fontanaFoodservice ? 6 : 12 }}>
                        <Box sx={{ bgcolor: TREND_COLORS.cardBg, borderRadius: 2, p: 2.5, border: `1px solid ${TREND_COLORS.divider}`, height: '100%' }}>
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                            <StorefrontIcon sx={{ color: TREND_COLORS.olive, fontSize: 20 }} />
                            <Typography sx={{ ...HEADING_SX, fontSize: '0.85rem', fontWeight: 700, color: TREND_COLORS.navy }}>DVH</Typography>
                          </Stack>
                          <Typography variant="body2" sx={{ color: TREND_COLORS.textSecondary, lineHeight: 1.75 }}>
                            {fontanaDvh}
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                    {fontanaFoodservice && (
                      <Grid size={{ xs: 12, md: fontanaDvh ? 6 : 12 }}>
                        <Box sx={{ bgcolor: TREND_COLORS.cardBg, borderRadius: 2, p: 2.5, border: `1px solid ${TREND_COLORS.divider}`, height: '100%' }}>
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                            <RestaurantIcon sx={{ color: TREND_COLORS.terracotta, fontSize: 20 }} />
                            <Typography sx={{ ...HEADING_SX, fontSize: '0.85rem', fontWeight: 700, color: TREND_COLORS.navy }}>FOODSERVICE</Typography>
                          </Stack>
                          <Typography variant="body2" sx={{ color: TREND_COLORS.textSecondary, lineHeight: 1.75 }}>
                            {fontanaFoodservice}
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                )}

                {(productConnections.length > 0 || article.linkedProducts.length > 0 || (isFoodArticle && (recipeConnections.length > 0 || article.linkedRecipes.length > 0))) && (
                  <Grid container spacing={3}>
                    {(productConnections.length > 0 || article.linkedProducts.length > 0) && (
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Box sx={{ mb: 1 }}>
                          <Typography sx={{ ...SECTION_HEADING_SX, mb: 2 }}>PRODUKTSPÅR</Typography>
                          {productConnections.length > 0 && (
                            <Stack spacing={1}>
                              {productConnections.map((entry) => (
                                <Typography key={entry} variant="body2" sx={{ color: TREND_COLORS.textSecondary, lineHeight: 1.7, pl: 2, borderLeft: `2px solid ${TREND_COLORS.olive}` }}>
                                  {entry}
                                </Typography>
                              ))}
                            </Stack>
                          )}
                          {article.linkedProducts.length > 0 && (
                            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
                              {article.linkedProducts.map((product) => (
                                <Chip key={product.assetId} label={product.title} size="small" sx={{ bgcolor: TREND_COLORS.oliveLight, color: TREND_COLORS.olive, fontWeight: 600, fontSize: '0.65rem' }} />
                              ))}
                            </Stack>
                          )}
                        </Box>
                      </Grid>
                    )}
                    {isFoodArticle && (recipeConnections.length > 0 || article.linkedRecipes.length > 0) && (
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Box sx={{ mb: 1 }}>
                          <Typography sx={{ ...SECTION_HEADING_SX, mb: 2 }}>RECEPTSPÅR</Typography>
                          {recipeConnections.length > 0 && (
                            <Stack spacing={1}>
                              {recipeConnections.map((entry) => (
                                <Typography key={entry} variant="body2" sx={{ color: TREND_COLORS.textSecondary, lineHeight: 1.7, pl: 2, borderLeft: `2px solid ${TREND_COLORS.terracotta}` }}>
                                  {entry}
                                </Typography>
                              ))}
                            </Stack>
                          )}
                          {article.linkedRecipes.length > 0 && (
                            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
                              {article.linkedRecipes.map((recipe) => (
                                <Chip key={recipe.assetId} label={recipe.title} size="small" sx={{ bgcolor: TREND_COLORS.terracottaLight, color: TREND_COLORS.terracotta, fontWeight: 600, fontSize: '0.65rem' }} />
                              ))}
                            </Stack>
                          )}
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                )}

              {/* Recipe section */}
              {isFoodArticle && recipe ? (
                <Box sx={{ mb: 4, bgcolor: TREND_COLORS.goldLight, borderRadius: 2, p: 3, border: `1px solid ${TREND_COLORS.gold}` }}>
                  <Typography sx={{ ...SECTION_HEADING_SX, mb: 1, borderBottomColor: TREND_COLORS.gold }}>
                    FONTANA-RECEPT
                  </Typography>
                  <Typography sx={{ ...HEADING_SX, fontSize: '1.1rem', fontWeight: 700, color: TREND_COLORS.navy, mb: 1 }}>
                    {recipe.adaptedTitle}
                  </Typography>
                  {recipe.description && (
                    <Typography variant="body2" sx={{ color: TREND_COLORS.textSecondary, lineHeight: 1.7, mb: 2, fontStyle: 'italic' }}>
                      {recipe.description}
                    </Typography>
                  )}
                  {recipe.servings && (
                    <Typography variant="caption" sx={{ color: TREND_COLORS.textMuted, display: 'block', mb: 2 }}>
                      Portioner: {recipe.servings}
                    </Typography>
                  )}
                  <Grid container spacing={3}>
                    <Grid size={{ xs: 12, md: 5 }}>
                      <Typography sx={{ ...HEADING_SX, fontSize: '0.8rem', fontWeight: 700, color: TREND_COLORS.navy, mb: 1 }}>
                        INGREDIENSER
                      </Typography>
                      <Stack spacing={0.5}>
                        {recipe.ingredients.map((ing, idx) => (
                          <Typography
                            key={idx}
                            variant="body2"
                            sx={{
                              color: TREND_COLORS.textPrimary,
                              fontWeight: ing.isFontanaProduct ? 700 : 400,
                            }}
                          >
                            {ing.amount} {ing.name}{ing.isFontanaProduct ? ' ★' : ''}
                          </Typography>
                        ))}
                      </Stack>
                    </Grid>
                    <Grid size={{ xs: 12, md: 7 }}>
                      <Typography sx={{ ...HEADING_SX, fontSize: '0.8rem', fontWeight: 700, color: TREND_COLORS.navy, mb: 1 }}>
                        INSTRUKTIONER
                      </Typography>
                      <Stack spacing={1}>
                        {recipe.instructions.map((step, idx) => (
                          <Typography key={idx} variant="body2" sx={{ color: TREND_COLORS.textSecondary, lineHeight: 1.7 }}>
                            {idx + 1}. {step}
                          </Typography>
                        ))}
                      </Stack>
                    </Grid>
                  </Grid>
                  {recipe.tips && (
                    <Typography variant="body2" sx={{ mt: 2, color: TREND_COLORS.textSecondary, fontStyle: 'italic' }}>
                      Tips: {recipe.tips}
                    </Typography>
                  )}
                  {recipe.fontanaProducts.length > 0 && (
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
                      {recipe.fontanaProducts.map((p) => (
                        <Chip key={p} label={p} size="small" sx={{ bgcolor: TREND_COLORS.gold, color: TREND_COLORS.navy, fontWeight: 600, fontSize: '0.65rem' }} />
                      ))}
                    </Stack>
                  )}
                </Box>
              ) : isFoodArticle && (
                <Box sx={{ mb: 4 }}>
                  <Button
                    variant="outlined"
                    disabled={recipeGenerating}
                    onClick={async () => {
                      if (!article) return;
                      setRecipeGenerating(true);
                      try {
                        const generated = await generateTrendRecipeForArticle(article._id);
                        setRecipe(generated);
                      } catch {
                        // Silently fail — button remains available
                      } finally {
                        setRecipeGenerating(false);
                      }
                    }}
                    sx={{
                      ...HEADING_SX,
                      fontSize: '0.75rem',
                      borderColor: TREND_COLORS.gold,
                      color: TREND_COLORS.navy,
                    }}
                  >
                    {recipeGenerating ? 'Genererar recept...' : '🍽 Skapa Fontana-recept'}
                  </Button>
                </Box>
              )}

                {/* Activation: turn the trend into a testable commercial move */}
                {hasActivationDetails && (
                  <Box sx={{ mb: 3 }}>
                    <Typography sx={{ ...SECTION_HEADING_SX, mb: 2 }}>AKTIVERING</Typography>
                    <Stack spacing={1.5}>
                      {(fontanaDepartmentOwner || fontanaTimeHorizon) && (
                        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                          {fontanaDepartmentOwner && (
                            <Chip
                              label={`Ägare: ${fontanaDepartmentOwner}`}
                              size="small"
                              sx={{ bgcolor: TREND_COLORS.softNavy, color: TREND_COLORS.navy, fontWeight: 600, fontSize: '0.65rem' }}
                            />
                          )}
                          {fontanaTimeHorizon && (
                            <Chip
                              label={`Tidshorisont: ${fontanaTimeHorizon}`}
                              size="small"
                              sx={{ bgcolor: TREND_COLORS.goldLight, color: TREND_COLORS.navy, fontWeight: 600, fontSize: '0.65rem' }}
                            />
                          )}
                        </Stack>
                      )}
                      {fontanaCommercialHypothesis && (
                        <Box>
                          <Typography variant="caption" sx={{ ...HEADING_SX, color: TREND_COLORS.textMuted, display: 'block', mb: 0.5 }}>
                            KOMMERSIELL HYPOTES
                          </Typography>
                          <Typography variant="body2" sx={{ color: TREND_COLORS.textSecondary, lineHeight: 1.7 }}>
                            {fontanaCommercialHypothesis}
                          </Typography>
                        </Box>
                      )}
                      {fontanaFirstTest && (
                        <Box>
                          <Typography variant="caption" sx={{ ...HEADING_SX, color: TREND_COLORS.textMuted, display: 'block', mb: 0.5 }}>
                            FÖRSTA TEST
                          </Typography>
                          <Typography variant="body2" sx={{ color: TREND_COLORS.textSecondary, lineHeight: 1.7 }}>
                            {fontanaFirstTest}
                          </Typography>
                        </Box>
                      )}
                      {fontanaEvidenceCaveat && (
                        <Box>
                          <Typography variant="caption" sx={{ ...HEADING_SX, color: TREND_COLORS.textMuted, display: 'block', mb: 0.5 }}>
                            BEVISFÖRBEHÅLL
                          </Typography>
                          <Typography variant="body2" sx={{ color: TREND_COLORS.textSecondary, lineHeight: 1.7, fontStyle: 'italic' }}>
                            {fontanaEvidenceCaveat}
                          </Typography>
                        </Box>
                      )}
                    </Stack>
                  </Box>
                )}

                {/* Design & packaging: concrete visual observations */}
                {hasDesignAnalysis && (
                  <Box sx={{ mb: 3 }}>
                    <Typography sx={{ ...SECTION_HEADING_SX, mb: 2 }}>DESIGN OCH FÖRPACKNING</Typography>
                    <Stack spacing={1.5}>
                      {([
                        ['VISUELL OBSERVATION', designObservedVisualCue],
                        ['HYLLPÅVERKAN', designShelfImpact],
                        ['FÖRPACKNINGSIMPLIKATION', designPackagingImplication],
                        ['VARUMÄRKESRISK', designBrandRisk],
                        ['EXEMPEL ATT BEVAKA', designExampleToWatch],
                      ] as Array<[string, string]>).filter(([, value]) => value).map(([heading, value]) => (
                        <Box key={heading}>
                          <Typography variant="caption" sx={{ ...HEADING_SX, color: TREND_COLORS.textMuted, display: 'block', mb: 0.5 }}>
                            {heading}
                          </Typography>
                          <Typography variant="body2" sx={{ color: TREND_COLORS.textSecondary, lineHeight: 1.7 }}>
                            {value}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                )}

                {/* Next steps */}
                {hasNextSteps && (
                  <Box sx={{ mb: 0 }}>
                    <Typography sx={{ ...SECTION_HEADING_SX, mb: 2 }}>NÄSTA STEG</Typography>
                    <Stack spacing={1.5}>
                      {recommendedActions.map((entry) => (
                        <Stack key={entry} direction="row" spacing={1.5} alignItems="flex-start">
                          <CheckCircleOutlineIcon sx={{ color: TREND_COLORS.olive, fontSize: 18, mt: 0.25, flexShrink: 0 }} />
                          <Typography variant="body2" sx={{ color: TREND_COLORS.textSecondary, lineHeight: 1.7 }}>
                            {entry}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </Box>
                )}
                {(article.signals.length > 0
                  || article.audienceSegments.length > 0
                  || article.consumerBehaviors.length > 0
                  || article.usageOccasions.length > 0
                  || article.designSignals.length > 0
                  || article.designSubCategory) && (
                  <Box>
                    <Typography variant="caption" sx={{ ...HEADING_SX, color: TREND_COLORS.textMuted, display: 'block', mb: 1 }}>
                      TRENDSPÅR, MÅLGRUPP OCH BETEENDEN
                    </Typography>
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                      {article.signals.map((entry) => (
                        <Chip key={`sig-${entry}`} label={entry} size="small" variant="outlined" sx={{ fontSize: '0.65rem', borderColor: TREND_COLORS.divider, color: TREND_COLORS.textMuted }} />
                      ))}
                      {article.audienceSegments.map((entry) => (
                        <Chip key={`aud-${entry}`} label={entry} size="small" sx={{ bgcolor: TREND_COLORS.softNavy, color: TREND_COLORS.navy, fontSize: '0.65rem' }} />
                      ))}
                      {article.consumerBehaviors.map((entry) => (
                        <Chip key={`beh-${entry}`} label={entry} size="small" sx={{ bgcolor: TREND_COLORS.goldLight, color: TREND_COLORS.navy, fontSize: '0.65rem' }} />
                      ))}
                      {article.usageOccasions.map((entry) => (
                        <Chip key={`occ-${entry}`} label={entry} size="small" sx={{ bgcolor: TREND_COLORS.oliveLight, color: TREND_COLORS.olive, fontSize: '0.65rem' }} />
                      ))}
                      {article.designSignals.map((entry) => (
                        <Chip key={`des-${entry}`} label={entry} size="small" sx={{ bgcolor: TREND_COLORS.terracottaLight, color: TREND_COLORS.terracotta, fontSize: '0.65rem' }} />
                      ))}
                      {article.designSubCategory && (
                        <Chip key="design-subcategory" label={getDesignSubCategoryLabel(article.designSubCategory)} size="small" sx={{ bgcolor: TREND_COLORS.cardBg, color: TREND_COLORS.textMuted, fontSize: '0.65rem' }} />
                      )}
                    </Stack>
                  </Box>
                )}

              {editable && (
                <Box sx={{ mb: 4 }}>
                  <Typography sx={{ ...SECTION_HEADING_SX, mb: 2 }}>MANUELL BILD</Typography>
                  <Stack spacing={1.5}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Direkt bild-URL"
                      value={heroImageDraft}
                      onChange={(event) => {
                        setHeroImageDraft(event.target.value);
                        setSaveState((prev) => ({ ...prev, error: null, success: null }));
                      }}
                      helperText="Klistra in en direkt bildlänk för att ersätta placeholdern i redaktionsläget."
                    />
                    {saveState.error && <Alert severity="error">{saveState.error}</Alert>}
                    {saveState.success && <Alert severity="success">{saveState.success}</Alert>}
                    <Stack direction="row" spacing={1}>
                      <Button
                        variant="contained"
                        disabled={saveState.loading}
                        onClick={() => void handleSaveHeroImage(heroImageDraft)}
                        sx={{ ...HEADING_SX, fontSize: '0.72rem', bgcolor: TREND_COLORS.navy, '&:hover': { bgcolor: TREND_COLORS.navyLight } }}
                      >
                        {saveState.loading ? 'Sparar...' : 'Spara bild'}
                      </Button>
                      <Button
                        variant="outlined"
                        disabled={saveState.loading}
                        onClick={() => void handleSaveHeroImage('')}
                        sx={{ ...HEADING_SX, fontSize: '0.72rem', borderColor: TREND_COLORS.divider, color: TREND_COLORS.navy }}
                      >
                        Rensa bild
                      </Button>
                    </Stack>
                  </Stack>
                </Box>
              )}

                <Divider sx={{ borderColor: TREND_COLORS.divider }} />

                <Box>
                  <Typography sx={{ ...SECTION_HEADING_SX, mb: 2 }}>KÄLLOR OCH BELÄGG</Typography>
                  <Stack spacing={2}>
                    <Box
                      sx={{
                        p: 2.5,
                        borderRadius: 2.5,
                        bgcolor: TREND_COLORS.cardBg,
                        border: `1px solid ${TREND_COLORS.divider}`,
                      }}
                    >
                      <Typography variant="caption" sx={{ ...HEADING_SX, color: TREND_COLORS.textMuted, display: 'block', mb: 1 }}>
                        KÄLLKONTEXT
                      </Typography>
                      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
                        <Chip size="small" variant="outlined" label={getArticleEvidenceStatusLabel(article.evidenceStatus)} />
                        <Chip size="small" variant="outlined" label={getArticleProvenanceLabel(article.provenance)} />
                        {typeof article.supportingSourceCount === 'number' && article.supportingSourceCount > 0 && (
                          <Chip size="small" variant="outlined" label={`${article.supportingSourceCount} stödjande källor`} />
                        )}
                        {typeof article.independentDomainCount === 'number' && article.independentDomainCount > 0 && (
                          <Chip size="small" variant="outlined" label={`${article.independentDomainCount} domäner`} />
                        )}
                        {typeof article.freshSourceCount === 'number' && article.freshSourceCount > 0 && (
                          <Chip size="small" variant="outlined" label={`${article.freshSourceCount} färska källor`} />
                        )}
                      </Stack>
                      {originalHeadline && originalHeadline !== article.title && (
                        <Typography variant="body2" sx={{ color: TREND_COLORS.navy, fontWeight: 700, lineHeight: 1.6, mb: 0.5 }}>
                          Originalrubrik: {originalHeadline}
                        </Typography>
                      )}
                      {evidenceMetaLine && (
                        <Typography variant="body2" sx={{ color: TREND_COLORS.textSecondary, lineHeight: 1.7 }}>
                          {evidenceMetaLine}
                        </Typography>
                      )}
                      {primarySourceExcerpt && (
                        <Typography
                          variant="body2"
                          sx={{ color: TREND_COLORS.textSecondary, lineHeight: 1.75, fontStyle: 'italic', mt: 1.25 }}
                        >
                          {primarySourceExcerpt}
                        </Typography>
                      )}
                    </Box>

                    {(sourceFacts.length > 0 || sourceWarnings.length > 0) && (
                      <Grid container spacing={2}>
                        {sourceFacts.length > 0 && (
                          <Grid size={{ xs: 12, md: 6 }}>
                            <Box sx={{ p: 2, borderRadius: 2, bgcolor: TREND_COLORS.cardBg, border: `1px solid ${TREND_COLORS.divider}`, height: '100%' }}>
                              <Typography variant="caption" sx={{ ...HEADING_SX, color: TREND_COLORS.textMuted, display: 'block', mb: 1.25 }}>
                                Nyckelfakta från källan
                              </Typography>
                              <Stack component="ul" spacing={1} sx={{ m: 0, pl: 2 }}>
                                {sourceFacts.map((entry) => (
                                  <Typography component="li" key={entry} variant="body2" sx={{ color: TREND_COLORS.textSecondary, lineHeight: 1.7 }}>
                                    {entry}
                                  </Typography>
                                ))}
                              </Stack>
                            </Box>
                          </Grid>
                        )}
                        {sourceWarnings.length > 0 && (
                          <Grid size={{ xs: 12, md: 6 }}>
                            <Box sx={{ p: 2, borderRadius: 2, bgcolor: '#FFF4D6', border: '1px solid #F0D18A', height: '100%' }}>
                              <Typography variant="caption" sx={{ ...HEADING_SX, color: '#8A5D00', display: 'block', mb: 1.25 }}>
                                Osäkerheter och begränsningar
                              </Typography>
                              <Stack component="ul" spacing={1} sx={{ m: 0, pl: 2 }}>
                                {sourceWarnings.map((entry) => (
                                  <Typography component="li" key={entry} variant="body2" sx={{ color: '#6E4C00', lineHeight: 1.7 }}>
                                    {entry}
                                  </Typography>
                                ))}
                              </Stack>
                            </Box>
                          </Grid>
                        )}
                      </Grid>
                    )}

                    {article.sourceContext.length > 0 && (
                      <Box sx={{ p: 2, borderRadius: 2, bgcolor: TREND_COLORS.cardBg, border: `1px solid ${TREND_COLORS.divider}` }}>
                        <Typography variant="caption" sx={{ ...HEADING_SX, color: TREND_COLORS.textMuted, display: 'block', mb: 1 }}>
                          Redaktionellt källkontext
                        </Typography>
                        <Stack spacing={0.75}>
                          {article.sourceContext.map((entry) => (
                            <Typography key={entry} variant="body2" sx={{ color: TREND_COLORS.textSecondary, lineHeight: 1.7 }}>
                              {entry}
                            </Typography>
                          ))}
                        </Stack>
                      </Box>
                    )}

                    {article.sourceItems && article.sourceItems.length > 0 && (
                      <Stack spacing={1.5}>
                        {article.sourceItems.map((sourceItem) => (
                          <Box
                            key={sourceItem._id}
                            sx={{
                              p: 2,
                              borderRadius: 2,
                              bgcolor: TREND_COLORS.cardBg,
                              border: `1px solid ${TREND_COLORS.divider}`,
                            }}
                          >
                            <Typography variant="body2" sx={{ color: TREND_COLORS.navy, fontWeight: 700 }}>
                              {sourceItem.extractionMetadata?.title || sourceItem.title || 'Källunderlag'}
                            </Typography>
                            <Typography variant="caption" sx={{ display: 'block', mt: 0.4, color: TREND_COLORS.textMuted }}>
                              {[
                                sourceItem.extractionMetadata?.siteName ? `Källa: ${sourceItem.extractionMetadata.siteName}` : '',
                                sourceItem.extractionMetadata?.byline ? `Byline: ${sourceItem.extractionMetadata.byline}` : '',
                              ].filter(Boolean).join(' · ')}
                            </Typography>
                            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1.2 }}>
                              <Chip size="small" variant="outlined" label={getSourceContentStatusLabel(sourceItem.contentStatus)} />
                              <Chip size="small" variant="outlined" label={getSourceReviewStatusLabel(sourceItem.reviewStatus)} />
                              <Chip size="small" variant="outlined" label={`${sourceItem.qualityAssessment.contentLength} tecken`} />
                              <Chip size="small" variant="outlined" label={`${sourceItem.qualityAssessment.longSentenceCount} längre meningar`} />
                              {sourceItem.extractionMetadata && (
                                <>
                                  <Chip size="small" variant="outlined" label={getSourceExtractionMethodLabel(sourceItem.extractionMetadata.method)} />
                                  <Chip size="small" variant="outlined" label={`${sourceItem.extractionMetadata.sectionCount} sektioner`} />
                                  {sourceItem.extractionMetadata.wasTruncated && (
                                    <Chip size="small" variant="outlined" label="Trunkerad extraktion" />
                                  )}
                                </>
                              )}
                            </Stack>
                            {sourceItem.extractionMetadata?.excerpt && (
                              <Typography variant="body2" sx={{ mt: 1.25, color: TREND_COLORS.textSecondary, lineHeight: 1.7, fontStyle: 'italic' }}>
                                {sourceItem.extractionMetadata.excerpt}
                              </Typography>
                            )}
                            {sourceItem.qualityAssessment.issue && (
                              <Typography variant="caption" sx={{ display: 'block', mt: 1, color: TREND_COLORS.textMuted }}>
                                {sourceItem.qualityAssessment.issue}
                              </Typography>
                            )}
                            {sourceItem.extractionMetadata?.warnings && sourceItem.extractionMetadata.warnings.length > 0 && (
                              <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: TREND_COLORS.textMuted }}>
                                Varningar: {sourceItem.extractionMetadata.warnings.join(', ')}
                              </Typography>
                            )}
                            {sourceItem.url && (
                              <Link
                                href={sourceItem.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                underline="hover"
                                sx={{ color: TREND_COLORS.navy, fontWeight: 600, fontSize: '0.8rem', display: 'inline-block', mt: 1 }}
                              >
                                Öppna källunderlag
                              </Link>
                            )}
                          </Box>
                        ))}
                      </Stack>
                    )}

                    {article.sourceReferences.length > 0 ? (
                      <Stack spacing={1.5}>
                        {article.sourceReferences.map((reference) => (
                          <Stack key={`${reference.title}-${reference.url}`} spacing={0.25}>
                            <Link
                              href={reference.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              underline="hover"
                              sx={{ color: TREND_COLORS.navy, fontWeight: 600, fontSize: '0.85rem' }}
                            >
                              {reference.title || reference.url}
                            </Link>
                            <Typography variant="caption" sx={{ color: TREND_COLORS.textMuted }}>
                              {[reference.sourceName, formatTrendDisplayDate(reference.publishedAt), reference.note]
                                .filter(Boolean)
                                .join(' · ')}
                            </Typography>
                          </Stack>
                        ))}
                      </Stack>
                    ) : (
                      <Typography variant="body2" sx={{ color: TREND_COLORS.textMuted }}>Inga källreferenser ännu.</Typography>
                    )}
                  </Stack>
                </Box>
              </Stack>
            </Stack>
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, bgcolor: TREND_COLORS.pageBg }}>
        {saveState.error && <Alert severity="error" sx={{ mr: 'auto' }}>{saveState.error}</Alert>}
        {saveState.success && <Alert severity="success" sx={{ mr: 'auto' }}>{saveState.success}</Alert>}
        {editable && (
          <Button
            variant="contained"
            disabled={saveState.loading}
            onClick={() => void handleSaveReview()}
            sx={{
              ...HEADING_SX,
              fontSize: '0.78rem',
              bgcolor: TREND_COLORS.navy,
              '&:hover': { bgcolor: TREND_COLORS.navyLight },
            }}
          >
            {saveState.loading ? 'Sparar...' : 'Spara och ompröva'}
          </Button>
        )}
        <Button
          onClick={onClose}
          sx={{
            ...HEADING_SX,
            fontSize: '0.8rem',
            color: TREND_COLORS.navy,
          }}
        >
          Stäng
        </Button>
      </DialogActions>
    </Dialog>
  );
}
